import { ActionMenu, ActionsMenuOption, ConsumeContainer, FullScreenContextProvider, NoData, useMeasureRef } from '@fox/atoms';
import { Skeleton } from '@mui/material';
import React, { ReactNode, useCallback, useMemo, useRef } from 'react';
import { FoxColumnType } from '../contracts/table';
import { HoverRowVisible } from '../data-grid-table/components/common';
import { DataGrid, VirtualPaginationOptions } from '../data-grid-table/data-grid';
import { Toolbar, ToolbarItems } from '../grid-toolbar';
import { DefaultPageSizeOptions } from '../pagination/index';
import { AutoColumnTitle } from './column-title';
import {
	AutoDataGridContextProvider,
	GridColPinnable,
	GridColVisible,
	GridFilterable,
	GridGroupable,
	GridOrder,
	GridSearchable,
	GridSortable,
	PivotContextProvider,
	useAutoDataGridContext
} from './context';

// re-export filters for consumption
export * from './context';
export * from './create-popover-button';
export * from './filters/date-range-filters';
export * from './filters/number-range-filter';
export * from './filters/text-filter';
export * from './settings-menu';

/** This is structurally very similar to the FoxTableColumn */
export type GraphColumn<T, TWrapProps = object> = {
	/** For columns that should be dealt with together (for hiding/unhiding) a title can be provided - when these columns are picked the grid will actually show the columns defined */
	groupTitle?: string;

	/** A string to uniquely identify the column index  */
	dataIndex: string;

	/**
	 * An optional key property that factors into the overall component key identity. The main use case is
	 * if you are feeding a memo-ized column set from a higher-level component and you require
	 * this new column set to apply despite having the same base (non-columnKey) key identity.
	 *
	 * If you are not feeding in a memo-ized column set, you do not need to set this property
	 */
	columnKey?: string;

	/** Alternate key used for sorting only */
	sortIndex?: string;

	/** The type of column this is - this way we know what filter/sorting component to apply */
	filterType?: 'string' | 'number' | 'datetime' | 'select' | 'treeselect' | 'link' | 'boolean' | 'ownermodel';

	/** Additional options for the given filter type */
	filterParams?: unknown;

	/* The title component - this can just be text or any react components to control this fully */
	title?: ReactNode;

	/* The description component - this must be text */
	description?: React.ReactNode;

	/** Indicate whether we can drag this column or not */
	canDrag?: boolean;

	/** The table supports resizing of columns, which this value is used on initial table load only. After that these values are stored in local storage permanently. */
	initialWidth: number;

	fixed?: 'left' | 'right' | 'toggle-left' | 'toggle-right';

	/** How to render the cell - this can be a complex component or just the text of the record. */
	render?: (value: any, record: T, index: number) => ReactNode;

	/** This is a special prop to be used for command columns to be visible on hover of the row only. For additional controls, you can use styled components and pass false to this. */
	hoverVisible?: boolean;

	/** The sort UI is visible */
	sortable?: boolean;

	/** The column may be hidden
	 * @default true
	 */
	hideable?: boolean;

	/** optional is default column - if any columns in an array are marked as defaultColumn=true a settings cog will appear in GraphQLAutoPagedGrid to show/hide columns */
	defaultColumn?: boolean;

	/** Ability to wrap the entire title component with custom UI. Note this MUST have a constant function identity, you can pass props using titleWrapProps */
	titleWrap?: React.ComponentType;

	/** Props to pass to the TitleWrap component */
	titleWrapProps?: TWrapProps;

	/** Ability to hide 3 dot menu title & title */
	hideTitle?: boolean;

	/** An optional footer component */
	columnFooter?: React.ReactNode;

	/** Adds padding on the left/right for a cell
	 * @default true
	 */
	padCell?: boolean;

	/** An optional function to group the column */
	groupByFunction?: (
		dataIndex: string,
		data: Array<T>
	) => Array<{ pivotName: string; pivotValue: string; pivotData: Array<T>; colour?: string }>;
};
export type AutoGridProps<T extends Record<string, any>> = {
	id: string;
	getRowKey: (row: T, index: number) => string;
	loading: boolean;
	data: T[];

	/** A handler that allows the data grid to update externally the pagination */
	virtualPage?: VirtualPaginationOptions;

	/** The total expected number of rows */
	total?: number;

	columns: GraphColumn<T>[];

	/** Allow the grid consumer to define a default sort if required */
	defaultSort?: SortState;

	noDataMessage?: string;
	rowClassName?: (row: T) => string | undefined;

	filterParams?: Record<string, unknown>;

	addColumnFilterParams?: (dataIndex: string, filterType: string) => Promise<string>;

	/** This function will be called on each row to build the context menu items for the 3 dot menu */
	buildRowActionMenu?: (row: T) => Array<ActionsMenuOption>;

	/** The column context menu appears on each column. The provided function will add options to be included on each respective column */
	buildColumnActionMenu?: (column: GraphColumn<T>) => Array<ActionsMenuOption>;

	/** The table itself can have an action context menu that will by default appear to the right of the toolbar */
	buildToolbarActionMenu?: () => Array<ActionsMenuOption>;

	buildActionIcons?: { icons: (row: T) => React.ReactNode[]; width: number };

	/** This is a component that will appear in the column header in the same pinned-right position as the row actions. TDC the actual type */
	rowActionTitle?: ReactNode;

	/** Remove toolbar from the table */
	hideToolbar?: boolean;

	scrollToIndex?: (func: (index: number) => Promise<any>) => void;

	hiddenColumnsFetch?: (cols: () => Array<string>) => void;

	/** Optional custom toolbar items */
	toolbar?: Array<ToolbarItems>;

	/** Toolbar with padding for pipelines and projects */
	paddedToolbar?: boolean;

	/**
	 * An explicit height to set for this grid, otherwise it will infer from the closest parent height context
	 */
	height?: number;

	/**
	 * Optional param for hiding table borders.
	 */
	hideColumnBorders?: boolean;

	/**
	 * Optional param for set the Row heigh. By default - 40px. Should be a string format
	 */
	rowHeight?: string;

	/** This is an extension component that will wrap/decorate the row and do something with the row data (e.g. wizard) */
	rowSelectionExtension?: (key: React.Key, record: T, children: React.ReactNode) => React.ReactNode;
	/**
	 * Save view information in local storage only
	 */
	localSaveOnly?: boolean;

	/**
	 * Optional parameter for applying filters to the data table without displaying them in the filter popover or visible filter list.
	 */
	silentFilters?: QueryFilter[];
} & GridFilterable &
	GridGroupable &
	GridSortable &
	GridColPinnable &
	GridColVisible &
	GridSearchable &
	GridOrder;

export type QueryFilter = {
	dataIndex: string;
	filterValue: string;
	filterType: string; // todo remove this
};

export type SortState = { column: string; asc?: boolean };

const AUTO_CONTEXT_MENU = 'autoContextMenu';

function Menu<T extends Record<string, any>>({
	buildRowActionMenu: useRowActionMenu,
	row,
	buildActionIcons
}: Pick<AutoGridProps<T>, 'buildRowActionMenu' | 'buildActionIcons'> & { row: T }) {
	// HACK - we have used useRowActionMenu as a hook in various grids, however ideally we actually use the non-hook / async functions ....
	// I am expecting by calling arguably invalid code, this will hint to the react compiler to not optimise this component
	const options = useRowActionMenu?.(row);
	return (
		<HoverRowVisible style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
			{buildActionIcons && buildActionIcons.icons(row)}
			{options ? <ActionMenu options={options} mainIconSize="20px" /> : null}
		</HoverRowVisible>
	);
}

export function InnerAutoDataGrid<T = Record<string, any>>({
	loading,
	columns,
	data,
	total,
	height,
	rowHeight,
	hideColumnBorders,
	getRowKey,
	noDataMessage,
	rowClassName,
	virtualPage,
	buildRowActionMenu,
	buildColumnActionMenu,
	buildToolbarActionMenu,
	buildActionIcons,
	hideToolbar,
	rowActionTitle,
	scrollToIndex,
	hiddenColumnsFetch,
	toolbar,
	rowSelectionExtension,
	paddedToolbar
}: AutoGridProps<T>) {
	const { pinnedColumnsDataIndex, sort, setOrder, setWidths, order, widths, hiddenColumnsDataIndex, groupBy } = useAutoDataGridContext();
	const tableRef = useRef(null);
	const cols: FoxColumnType<T>[] = useMemo(() => {
		const indexes = columns.map(a => a.dataIndex).filter(a => !!a && !hiddenColumnsDataIndex.includes(a));

		// whenever the columns change, we can register our new function that specifies this
		hiddenColumnsFetch?.(() => hiddenColumnsDataIndex);

		// handle the case where sort state is out of sync with the actual columns of the grid
		const allCols = [...order.filter(a => indexes.includes(a)), ...indexes.filter(a => !order.includes(a))];
		// add the action column if required
		(buildRowActionMenu || buildActionIcons) && allCols.push(AUTO_CONTEXT_MENU);
		return allCols.map(o => {
			const c = columns.find(a => a.dataIndex === o);

			if (!c && o === AUTO_CONTEXT_MENU) {
				return {
					dataIndex: 'actions',
					width: (buildRowActionMenu ? 45 : 0) + (buildActionIcons ? buildActionIcons.width : 0),
					fixed: 'right',
					padCell: false,
					title: rowActionTitle ? rowActionTitle : <></>,
					render: (_, row, __) => <Menu row={row} buildRowActionMenu={buildRowActionMenu} buildActionIcons={buildActionIcons} />
				} as FoxColumnType<T>;
			}
			const columnPinned = pinnedColumnsDataIndex.includes(c.dataIndex);
			const fixTo =
				c.fixed == 'left' || (columnPinned && c.fixed == 'toggle-left')
					? 'left'
					: c.fixed == 'right' || (columnPinned && c.fixed == 'toggle-right')
						? 'right'
						: c.fixed === undefined
							? columnPinned
								? 'left'
								: undefined
							: undefined;
			return {
				columnKey: c.columnKey,
				dataIndex: c.dataIndex,
				width: widths[c.dataIndex] ?? c.initialWidth,
				padCell: typeof c.padCell === 'boolean' ? c.padCell : true,
				fixed: fixTo,
				columnFooter: c.columnFooter,
				render: c.hoverVisible
					? (a, b, i) => {
							const rendered = c.render(a, b, i);
							return <HoverRowVisible>{rendered}</HoverRowVisible>;
						}
					: c.render,
				title: c.hideTitle ? (
					<></>
				) : (
					<AutoColumnTitle
						col={c}
						key={c.dataIndex}
						buildOptionsMenu={buildColumnActionMenu}
						hideToolbar={hideToolbar}
						hidePinAndHide={columns.length <= 1}
					/>
				)
			} as FoxColumnType<T>;
		});
	}, [columns, sort, widths, order, pinnedColumnsDataIndex, hiddenColumnsDataIndex /*, buildActionIcons, buildRowActionMenu*/]);

	const updateColumnSizes = useCallback(
		function updateColumnSizes(val: FoxColumnType<T>[]) {
			const w = val.reduce((prev, cur) => ({ ...prev, [cur.dataIndex]: cur.width }), {} as Record<string, number>);
			setWidths(w);
		},
		[setWidths]
	);

	const updateColumnOrder = useCallback(
		function updateColumnOrder(val: FoxColumnType<T>[]) {
			setOrder(val.map(a => a.dataIndex));
		},
		[setOrder]
	);

	let pivotData: { pivotName: string; pivotValue: string; pivotData: T[]; colour?: string }[] = [];
	if (groupBy) {
		pivotData = columns.find(c => c.dataIndex === groupBy)?.groupByFunction(groupBy, data);
	}

	const [ref, measure] = useMeasureRef();

	// columns coming in are slightly different to what AutoDataGrid expects fortunately the conversion is quite easy
	return (
		<FullScreenContextProvider containerElementRef={tableRef}>
			<ConsumeContainer measureConsumption={measure}>
				<div ref={tableRef}>
					{!hideToolbar && (
						<Toolbar
							ref={ref}
							paddedToolbar={paddedToolbar}
							data={data}
							total={total}
							items={toolbar}
							columns={columns}
							buildToolbarActionMenu={buildToolbarActionMenu}
						/>
					)}

					{pivotData?.length < 1 ? (
						<DataGrid
							data={data}
							getRowKey={getRowKey}
							total={total}
							columns={cols}
							height={height}
							virtualPage={virtualPage}
							rowClassName={rowClassName}
							loadingData={loading}
							noDataPlaceHolder={noDataMessage ? <NoData message={noDataMessage} /> : undefined}
							onColumnOrderChanged={updateColumnOrder}
							onColumnResized={updateColumnSizes}
							scrollToIndex={scrollToIndex}
							rowSelectionExtension={rowSelectionExtension}
							rowHeight={rowHeight}
							hideColumnBorders={hideColumnBorders}
						/>
					) : (
						<div style={{ height: '100%', overflowY: 'auto' }}>
							{pivotData?.map(pivot => {
								return (
									<div style={{ marginBottom: 10 }} key={pivot.pivotName}>
										<div
											style={{
												backgroundColor: pivot?.colour,
												color: 'black',
												padding: 5,
												borderRadius: 5,
												marginBottom: 10
											}}
										>
											{pivot.pivotName}
										</div>
										<PivotContextProvider value={{ groupBy, groupByValue: pivot.pivotValue }}>
											<DataGrid
												maxHeight={Math.min(800, window.innerHeight - 200)}
												data={pivot.pivotData}
												getRowKey={getRowKey}
												total={pivot.pivotData.length}
												columns={cols}
												virtualPage={virtualPage}
												rowClassName={rowClassName}
												loadingData={loading}
												noDataPlaceHolder={noDataMessage ? <NoData message={noDataMessage} /> : undefined}
												onColumnOrderChanged={updateColumnOrder}
												onColumnResized={updateColumnSizes}
												scrollToIndex={scrollToIndex}
												rowSelectionExtension={rowSelectionExtension}
												rowHeight={rowHeight}
											/>
										</PivotContextProvider>
									</div>
								);
							})}
						</div>
					)}
				</div>
			</ConsumeContainer>
		</FullScreenContextProvider>
	);
}

export function AutoDataGrid<T = Record<string, any>>({
	loading,
	columns,
	defaultSort,
	filters,
	setFilters,
	onSort,
	sort,
	filterParams,
	addColumnFilterParams,
	id,
	search,
	setSearch,
	pinnedColumnsDataIndex,
	setPinnedColumnsDataIndex,
	colOrder,
	setColOrder,
	colVis,
	setColVis,
	groupBy,
	setGroupBy,
	localSaveOnly,
	rowHeight,
	hideColumnBorders,
	...rest
}: AutoGridProps<T>) {
	return (
		<AutoDataGridContextProvider
			sort={sort ?? defaultSort}
			onSort={onSort}
			filters={filters}
			setFilters={setFilters}
			filterParams={filterParams}
			addColumnFilterParams={addColumnFilterParams}
			loading={loading}
			columns={columns}
			id={id}
			search={search}
			setSearch={setSearch}
			pinnedColumnsDataIndex={pinnedColumnsDataIndex}
			setPinnedColumnsDataIndex={setPinnedColumnsDataIndex}
			colOrder={colOrder}
			setColOrder={setColOrder}
			colVis={colVis}
			setColVis={setColVis}
			groupBy={groupBy}
			setGroupBy={setGroupBy}
			localSaveOnly={localSaveOnly}
		>
			<InnerAutoDataGrid
				{...rest}
				id={id}
				loading={loading}
				columns={columns}
				rowHeight={rowHeight}
				hideColumnBorders={hideColumnBorders}
			/>
		</AutoDataGridContextProvider>
	);
}

/** This builds and tracks pagination object with an optional callback to pass up the page changes
 * @param updatePage An optional callback to notify the consumer of pagination that the value has changed.
 */
export function usePagination(updatePage?: (page: number) => void) {
	const [currentPage, setCurrentPage] = React.useState(0);
	const [pageSize, setPageSize] = React.useState(20);
	return useMemo(
		() => ({
			current: currentPage + 1,
			pageSize,
			pageSizeOptions: DefaultPageSizeOptions,
			onChange: (page, pageSize) => {
				setPageSize(pageSize);
				setCurrentPage(page - 1);
				updatePage?.(page);
			}
		}),
		[currentPage, pageSize, updatePage]
	);
}

export type LazyCellProps<TKey, TResolvedValue> = {
	value: TKey;
	onResolveValue: (value: TKey) => Promise<TResolvedValue>;
	renderResolvedValue: (rv: TResolvedValue) => React.ReactNode;
	renderUnresolvedValue: (e: Error) => React.ReactNode;
};

export function LazyCell<T, U>({ value, onResolveValue, renderResolvedValue, renderUnresolvedValue }: LazyCellProps<T, U>) {
	const mounted = React.useRef(false);
	const [resolvedValue, setResolvedValue] = React.useState(undefined);
	const [unresolvedError, setUnresolvedError] = React.useState<Error | undefined>(undefined);
	React.useEffect(() => {
		mounted.current = true;
		onResolveValue(value)
			.then(r => {
				if (mounted.current) setResolvedValue(r);
			})
			.catch(e => {
				if (mounted.current) setUnresolvedError(e);
			});
		return () => {
			mounted.current = false;
		};
	}, []);
	return unresolvedError ? (
		<>{renderUnresolvedValue(unresolvedError)}</>
	) : resolvedValue ? (
		<>{renderResolvedValue(resolvedValue)}</>
	) : (
		<Skeleton />
	);
}
